import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import { api } from '@/hooks/useApi';

export interface AthleteProfile {
  id: string;
  name: string;
  nameEn?: string;
  gender: string;
  dateOfBirth: string;
  ethnicity: string;
  nationalId?: string;
  phone?: string;
  email?: string;
  address?: string;
  emergencyContact?: string;
  emergencyPhone?: string;
  photoUrl?: string;
  township: string;
  eventId: string;
  eventName: string;
  registrationStatus: 'PENDING' | 'APPROVED' | 'REJECTED';
}

export interface AthleteAuthContextValue {
  athlete: AthleteProfile | null;
  token: string | null;
  isLoading: boolean;
  mustChangePassword: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  setToken: (token: string) => void;
  refreshProfile: () => Promise<void>;
}

const AthleteAuthContext = createContext<AthleteAuthContextValue | undefined>(undefined);

const TOKEN_KEY = 'atayal-athlete-token';

export function AthleteAuthProvider({ children }: { children: ReactNode }) {
  const [athlete, setAthlete] = useState<AthleteProfile | null>(null);
  const [token, setTokenState] = useState<string | null>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(TOKEN_KEY);
    }
    return null;
  });
  const [isLoading, setIsLoading] = useState(true);
  const [mustChangePassword, setMustChangePassword] = useState(false);

  const setToken = useCallback((newToken: string) => {
    setTokenState(newToken);
    localStorage.setItem(TOKEN_KEY, newToken);
  }, []);

  const logout = useCallback(() => {
    setTokenState(null);
    setAthlete(null);
    setMustChangePassword(false);
    localStorage.removeItem(TOKEN_KEY);
  }, []);

  const refreshProfile = useCallback(async () => {
    if (!token) return;
    try {
      const res = await api.get('/me/profile', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = res.data?.data ?? res.data;
      setAthlete(data);
      setMustChangePassword(false);
    } catch {
      logout();
    }
  }, [token, logout]);

  const login = useCallback(
    async (username: string, password: string) => {
      const res = await api.post('/athlete-auth/login', { username, password });
      const data = res.data?.data ?? res.data;
      const newToken = data.token as string;

      setToken(newToken);

      if (data.mustChangePassword) {
        setMustChangePassword(true);
        setAthlete(null);
      } else {
        setMustChangePassword(false);
        // Fetch profile with the new token
        try {
          const profileRes = await api.get('/me/profile', {
            headers: { Authorization: `Bearer ${newToken}` },
          });
          const profileData = profileRes.data?.data ?? profileRes.data;
          setAthlete(profileData);
        } catch {
          // Profile fetch failed but login succeeded
        }
      }
    },
    [setToken],
  );

  // Verify token on mount
  useEffect(() => {
    if (!token) {
      setIsLoading(false);
      return;
    }

    api
      .get('/me/profile', {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        const data = res.data?.data ?? res.data;
        setAthlete(data);
        setMustChangePassword(false);
      })
      .catch(() => {
        logout();
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <AthleteAuthContext.Provider
      value={{
        athlete,
        token,
        isLoading,
        mustChangePassword,
        login,
        logout,
        setToken,
        refreshProfile,
      }}
    >
      {children}
    </AthleteAuthContext.Provider>
  );
}

export function useAthleteAuth(): AthleteAuthContextValue {
  const context = useContext(AthleteAuthContext);
  if (!context) {
    throw new Error('useAthleteAuth must be used within an AthleteAuthProvider');
  }
  return context;
}
